import React, { useMemo } from 'react';
import { Clock, Hash } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getSessionAnalytics } from '../../lib/analytics';
import { SectionHeader, Card, ChartTooltip, MetricTile, EmptyState, CHART_COLORS } from './shared';

/**
 * APEX v7.4.4 — Sessions analytics.
 *
 * Retrait de la section "Impact du Mental Global" et de l'insight "Impact mental massif"
 * (puisque le mental n'est plus saisi manuellement depuis v7.4.2).
 */
export default function SessionsSection({ sessions }) {
  const data = useMemo(() => getSessionAnalytics(sessions), [sessions]);

  if (!data || sessions.length === 0) {
    return <EmptyState message="Aucune session fermée" sub="Ferme des sessions pour voir apparaître les analytics" />;
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Sessions" value={data.totalSessions} color="white" />
        <MetricTile label="Games Totales" value={data.totalGames} color="white" />
        <MetricTile
          label="Sessions Positives"
          value={data.positiveSessions}
          subtext={`${Math.round(data.positiveSessions / data.totalSessions * 100)}%`}
          color="cyan"
        />
        <MetricTile
          label="Winrate Global"
          value={`${Math.round((data.totalWins / data.totalGames) * 100) || 0}%`}
          subtext={`${data.totalWins} W / ${data.totalGames - data.totalWins} L`}
          color="fuchsia"
        />
      </div>

      {/* Timeline */}
      {data.timeline.length >= 2 && (
        <Card>
          <SectionHeader title="Winrate Par Session" subtitle="Historique chronologique" icon={Clock} />
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="sessionNumber" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} domain={[0, 100]} />
              <Tooltip content={<ChartTooltip />} />
              <Line type="monotone" dataKey="winrate" stroke={CHART_COLORS.cyan} strokeWidth={2} dot={{ fill: CHART_COLORS.cyan, r: 4 }} name="Winrate %" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Impact taille de session */}
      <Card>
        <SectionHeader title="Impact de la Taille de Session" subtitle="1 vs 2 vs 3 vs 4+ games" icon={Hash} />
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.sizeImpact}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="size" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
            <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} domain={[0, 100]} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="winrate" fill={CHART_COLORS.fuchsia} name="Winrate %" />
          </BarChart>
        </ResponsiveContainer>
        <div className="text-[10px] text-zinc-500 font-mono mt-2">
          Indice sur ta règle "max 3 ranked/session" : vois si tes sessions 4+ performent moins bien.
        </div>
      </Card>
    </div>
  );
}
